import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from '@aimsos/hooks';

import {
  DEFAULT_SORT_DIRECTION,
  DEFAULT_SORT_FIELD,
  SEARCH_DEBOUNCE_MS,
} from '../constants/widgetLibraryFilters.js';

const initialState = {
  category: '',
  status: '',
  type: '',
  governed: '',
  profile: '',
};

/**
 * Single source of truth for the library's search, filters and sort (CT-5186).
 *
 * The whole point of this hook is that **nothing filters locally**: it produces
 * a memoised `query` that `useWidgetCatalog` spreads into the request body, so
 * the service (and later the endpoint) does the narrowing. A stable identity
 * matters — `query` is a dependency of the fetch, so it is memoised on
 * primitives only.
 *
 * The bar's chip slots commit immediately. The "All filters" slideout is
 * **staged**: it edits `draft`, and only `applyDraft()` moves that into the
 * committed state, so dismissing it changes nothing.
 *
 * Every dimension is a single-select scalar, matching the prototype's
 * radio-list slideout sections, and each is adapted here into the shape
 * `POST /v0/dashboard/widget/list` takes: `categoryKeys` / `typeCodes` are
 * lists on the wire — as is `sectionKeys`, which the Profile dimension feeds from
 * `section/list_filter` — so a scalar goes out as a one-element array and a future
 * multi-select needs no service change; `isGoverned` is a boolean, so the
 * tri-state string this hook holds is converted at the boundary rather than
 * leaking `'true'` into the request body.
 */
export const useWidgetLibraryFilters = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [committed, setCommitted] = useState(initialState);
  const [draft, setDraft] = useState(initialState);

  // One request per settled query, not one per keystroke: `Filters` v1.4.1 has
  // no `searchBar` slot, so `searchProps` fires on every character and the
  // debounce has to sit between the box and the request body.
  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  // Clearing is not a keystroke and must not wait for the timer. `Clear all` and
  // emptying the box both drop `searchTerm` to '' immediately; reading the
  // debounced value here would keep sending the old term for another 400ms, so
  // the no-results `Clear all filters` button would appear to do nothing.
  const effectiveSearch = searchTerm.trim() === '' ? '' : debouncedSearch.trim();

  const { category, status, type, governed, profile } = committed;
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);

  const query = useMemo(
    () => ({
      orderBy: sortField,
      orderDirection: sortDirection,
      filters: {
        ...(category ? { categoryKeys: [category] } : {}),
        ...(type ? { typeCodes: [type] } : {}),
        ...(status ? { status } : {}),
        ...(governed ? { isGoverned: governed === 'true' } : {}),
        ...(profile ? { sectionKeys: [profile] } : {}),
      },
      searchKeyword: effectiveSearch || undefined,
    }),
    [category, effectiveSearch, governed, profile, sortDirection, sortField, status, type],
  );

  const activeFilterCount = useMemo(
    () => (category ? 1 : 0) + (status ? 1 : 0) + (type ? 1 : 0) + (governed ? 1 : 0)
      + (profile ? 1 : 0),
    [category, governed, profile, status, type],
  );

  const hasActiveFilters = activeFilterCount > 0 || effectiveSearch.length > 0;

  const setCommittedField = useCallback((field, value) => {
    setCommitted((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Bar slots are array-valued; the page keeps single-select dimensions as
  // scalars, so the adapter lives here rather than in the component.
  const setCategoryFromSlot = useCallback(
    (next) => setCommittedField('category', next[0] ?? ''),
    [setCommittedField],
  );
  const setStatusFromSlot = useCallback(
    (next) => setCommittedField('status', next[0] ?? ''),
    [setCommittedField],
  );
  // Type/Governed/Profile never render as bar chips (only Category/Status do, per
  // DS-2), but the bar still registers them as hidden `Filters` slots so the
  // DS's own "Clear filters" link reacts to a slideout-only selection too —
  // see WidgetLibraryFilterBar. Same array adapter as the two above.
  const setTypeFromSlot = useCallback(
    (next) => setCommittedField('type', next[0] ?? ''),
    [setCommittedField],
  );
  const setGovernedFromSlot = useCallback(
    (next) => setCommittedField('governed', next[0] ?? ''),
    [setCommittedField],
  );
  const setProfileFromSlot = useCallback(
    (next) => setCommittedField('profile', next[0] ?? ''),
    [setCommittedField],
  );

  const setDraftField = useCallback((field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  /** Seed the draft from the committed state — call this when the slideout opens. */
  const openDraft = useCallback(() => {
    setDraft(committed);
  }, [committed]);

  const applyDraft = useCallback(() => {
    setCommitted(draft);
  }, [draft]);

  const clearDraft = useCallback(() => {
    setDraft(initialState);
  }, []);

  const clearAll = useCallback(() => {
    setSearchTerm('');
    setCommitted(initialState);
    setDraft(initialState);
    setSortField(DEFAULT_SORT_FIELD);
    setSortDirection(DEFAULT_SORT_DIRECTION);
  }, []);

  return useMemo(
    () => ({
      query,
      searchTerm,
      setSearchTerm,
      committed,
      draft,
      setDraftField,
      openDraft,
      applyDraft,
      clearDraft,
      setCategoryFromSlot,
      setStatusFromSlot,
      setTypeFromSlot,
      setGovernedFromSlot,
      setProfileFromSlot,
      sortField,
      sortDirection,
      setSortField,
      setSortDirection,
      activeFilterCount,
      hasActiveFilters,
      clearAll,
    }),
    [
      query, searchTerm, committed, draft, setDraftField, openDraft, applyDraft,
      clearDraft, setCategoryFromSlot, setStatusFromSlot, setTypeFromSlot, setGovernedFromSlot,
      setProfileFromSlot,
      sortField, sortDirection, activeFilterCount, hasActiveFilters, clearAll,
    ],
  );
};
